import { useEffect, useState } from "react";
import { BadgeCheck, Bot, ClipboardList, Database, FileText, FlaskConical, HelpCircle, Image as ImageIcon, Key, Megaphone, Package, PenLine, RotateCcw, Settings, ShieldCheck, ShoppingBag, Star, Store, Tags, Trash2, TrendingUp, User, UserCheck, Users, Youtube } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, fmtTL } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PagesTab from "../components/admin/PagesTab";
import ContentTab from "../components/admin/ContentTab";
import CategoriesTab from "../components/admin/CategoriesTab";
import ProductsTab from "../components/admin/ProductsTab";
import RfqsTab from "../components/admin/RfqsTab";
import OrdersTab from "../components/admin/OrdersTab";
import ReportsTab from "../components/admin/ReportsTab";
import QuestionsTab from "../components/admin/QuestionsTab";
import ApiKeysTab from "../components/admin/ApiKeysTab";
import PrivacyTab from "../components/admin/PrivacyTab";
import AdsTab from "../components/admin/AdsTab";
import FeaturedTab from "../components/admin/FeaturedTab";
import SupportTab from "../components/admin/SupportTab";
import ComplaintsTab from "../components/admin/ComplaintsTab";
import ReturnsTab from "../components/admin/ReturnsTab";
import RegistrationsTab from "../components/admin/RegistrationsTab";
import LogoTab from "../components/admin/LogoTab";
import FaqTab from "../components/admin/FaqTab";
import VideosTab from "../components/admin/VideosTab";
import ProfileTab from "../components/admin/ProfileTab";
import UsersTab from "../components/admin/UsersTab";
import RecycleBinTab from "../components/admin/RecycleBinTab";
import TestDataTab from "../components/admin/TestDataTab";
import BackupTab from "../components/admin/BackupTab";
import AiTab from "../components/admin/AiTab";
import AdminAccountTab from "../components/admin/AdminAccountTab";

const TABS = [
    ["categories", "Kategoriler", Tags],
    ["products", "Ürünler", Package],
    ["rfqs", "Teklifler", FileText],
    ["orders", "Siparişler", ShoppingBag],
    ["reports", "Raporlar", TrendingUp],
    ["questions", "Sorular", HelpCircle],
    ["apikeys", "API Erişimi", Key],
    ["registrations", "Başvurular", ClipboardList],
    ["ads", "Reklamlar", Megaphone],
    ["featured", "Kampanya Onayı", Star],
    ["support", "Destek", UserCheck],
    ["complaints", "Şikayet & Ödül", BadgeCheck],
    ["returns", "İade Talepleri", RotateCcw],
    ["pages", "Sayfalar", FileText],
    ["content", "Site Metinleri", PenLine],
    ["faq", "SSS", HelpCircle],
    ["videos", "Videolar", Youtube],
    ["logo", "Site Logosu", ImageIcon],
    ["profil", "Profil Bilgilerim", User],
    ["users", "Kullanıcılar", Users],
    ["privacy", "Gizlilik Ayarları", ShieldCheck],
    ["recycle", "Geri Dönüşüm", Trash2],
    ["testdata", "Test Verileri", FlaskConical],
    ["backup", "Yedekleme", Database],
    ["ai", "AI Yönetimi", Bot],
    ["admininfo", "Admin Bilgileri", ShieldCheck],
];

export default function AdminPanel() {
    const [overview, setOverview] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") || "categories";
    useEffect(() => { api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);

    const kpis = overview ? [
        { label: "Tedarikçi", value: overview.supplier_count, sub: `${overview.verified_suppliers} doğrulanmış`, icon: Store, tid: "admin-kpi-suppliers" },
        { label: "Aktar Mağaza", value: overview.store_count, sub: `${overview.user_count} kullanıcı`, icon: ShoppingBag, tid: "admin-kpi-stores" },
        { label: "Platform Ciro (GMV)", value: fmtTL(overview.gmv), sub: `${overview.order_count} sipariş`, icon: TrendingUp, tid: "admin-kpi-gmv" },
        { label: "Açık İşler", value: overview.open_orders + overview.open_rfqs, sub: `${overview.open_orders} sipariş • ${overview.open_rfqs} RFQ • ${overview.pending_registrations} başvuru`, icon: FileText, tid: "admin-kpi-open" },
    ] : [];

    return (
        <div className="space-y-6" data-testid="admin-panel-page">
            <div className="flex items-center gap-3">
                <Settings className="h-7 w-7 text-terra-600" />
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Yönetim Paneli</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Platform yönetimi — kategoriler, ürünler, teklifler, siparişler, raporlar, reklamlar, destek, gizlilik.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {kpis.map((k, i) => (
                    <div key={k.tid} className="animate-fade-up rounded-2xl border bg-card p-5 shadow-sm" style={{ animationDelay: `${i * 0.05}s` }} data-testid={k.tid}>
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k.label}</p>
                            <k.icon className="h-4 w-4 text-terra-500" />
                        </div>
                        <p className="mt-2 font-display text-2xl font-extrabold">{k.value}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{k.sub}</p>
                    </div>
                ))}
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })} data-testid="admin-tabs">
                <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
                    {TABS.map(([v, label, Icon]) => (
                        <TabsTrigger key={v} value={v} className="border data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={`admin-tab-${v}`}>
                            <Icon className="mr-1.5 h-3.5 w-3.5" /> {label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
                <TabsContent value="products" className="mt-4"><ProductsTab /></TabsContent>
                <TabsContent value="rfqs" className="mt-4"><RfqsTab /></TabsContent>
                <TabsContent value="orders" className="mt-4"><OrdersTab /></TabsContent>
                <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
                <TabsContent value="questions" className="mt-4"><QuestionsTab /></TabsContent>
                <TabsContent value="apikeys" className="mt-4"><ApiKeysTab /></TabsContent>
                <TabsContent value="registrations" className="mt-4"><RegistrationsTab /></TabsContent>
                <TabsContent value="ads" className="mt-4"><AdsTab /></TabsContent>
                <TabsContent value="featured" className="mt-4"><FeaturedTab /></TabsContent>
                <TabsContent value="support" className="mt-4"><SupportTab /></TabsContent>
                <TabsContent value="complaints" className="mt-4"><ComplaintsTab /></TabsContent>
                <TabsContent value="returns" className="mt-4"><ReturnsTab /></TabsContent>
                <TabsContent value="pages" className="mt-4"><PagesTab /></TabsContent>
                <TabsContent value="content" className="mt-4"><ContentTab /></TabsContent>
                <TabsContent value="faq" className="mt-4"><FaqTab /></TabsContent>
                <TabsContent value="videos" className="mt-4"><VideosTab /></TabsContent>
                <TabsContent value="logo" className="mt-4"><LogoTab /></TabsContent>
                <TabsContent value="profil" className="mt-4"><ProfileTab /></TabsContent>
                <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
                <TabsContent value="recycle" className="mt-4"><RecycleBinTab /></TabsContent>
                <TabsContent value="testdata" className="mt-4"><TestDataTab /></TabsContent>
                <TabsContent value="backup" className="mt-4"><BackupTab /></TabsContent>
                <TabsContent value="ai" className="mt-4"><AiTab /></TabsContent>
                <TabsContent value="admininfo" className="mt-4"><AdminAccountTab /></TabsContent>
                <TabsContent value="privacy" className="mt-4"><PrivacyTab /></TabsContent>
            </Tabs>
        </div>
    );
}
